import { useId, useRef, useState } from 'react';
import { ArdoqId, APIWorkspaceAttributes } from '@ardoq/api-types';
import { FlexBox, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { Icon, IconName, IconSize } from '@ardoq/icons';
import { GhostButton } from '@ardoq/button';
import { ControlledPopover } from '@ardoq/popovers';
import { colors } from '@ardoq/design-tokens';
import { pluralize } from '@ardoq/common-helpers';
import { type I18nLocale, t } from '@ardoq/i18n';
import type { GetWorkspaceManagerLinkProps } from './types';

const WorkspaceIcon = () => (
  <Icon
    iconName={IconName.WORKSPACE}
    iconSize={IconSize.SMALL}
    color={colors.icon}
  />
);

export type WorkspaceManagerLinksProps = {
  workspaces: APIWorkspaceAttributes[];
  canManageWorkspace: (workspaceId: ArdoqId) => boolean;
  getWorkspaceManagerLinkProps: GetWorkspaceManagerLinkProps;
  i18nLocale: I18nLocale;
};

export const WorkspaceManagerLinks = ({
  workspaces,
  canManageWorkspace,
  getWorkspaceManagerLinkProps,
  i18nLocale,
}: WorkspaceManagerLinksProps) => {
  const [isWorkspacesPopoverOpen, setIsWorkspacesPopoverOpen] = useState(false);
  const workspacesPopoverId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  const singleWorkspace = workspaces.length === 1 ? workspaces[0] : null;

  if (singleWorkspace) {
    if (!canManageWorkspace(singleWorkspace._id)) {
      return (
        <FlexBox gap="xsmall" align="center">
          <WorkspaceIcon />
          <Text variant="text2">{singleWorkspace.name}</Text>
        </FlexBox>
      );
    }

    return (
      <FlexBox gap="xsmall" align="center">
        <WorkspaceIcon />
        <Link
          hideIcon
          typography="text2"
          {...getWorkspaceManagerLinkProps(
            singleWorkspace._id,
            'component-types'
          )}
        >
          {t(
            i18nLocale,
            'inventory:datasourceInformation.workspaceManager',
            'Workspace manager'
          )}
        </Link>
      </FlexBox>
    );
  }

  return (
    <FlexBox gap="medium" align="center">
      <ControlledPopover
        id={workspacesPopoverId}
        isOpen={isWorkspacesPopoverOpen}
        onClose={() => {
          setIsWorkspacesPopoverOpen(false);
          triggerRef.current?.focus();
        }}
        onClickOutside={() => setIsWorkspacesPopoverOpen(false)}
        content={
          <Stack gap="xsmall">
            <Text variant="text2" color="textSubtle">
              {t(
                i18nLocale,
                'inventory:datasourceInformation.featuresInWorkspaces',
                { count: workspaces.length },
                `This component type features in ${workspaces.length} ${pluralize(
                  'workspace',
                  workspaces.length
                )}:`
              )}
            </Text>
            {workspaces.map(workspace => (
              <FlexBox key={workspace._id} gap="xsmall" align="center">
                <WorkspaceIcon />
                {canManageWorkspace(workspace._id) ? (
                  <Link
                    hideIcon
                    typography="text2"
                    {...getWorkspaceManagerLinkProps(
                      workspace._id,
                      'component-types'
                    )}
                  >
                    {workspace.name}
                  </Link>
                ) : (
                  <Text variant="text2">{workspace.name}</Text>
                )}
              </FlexBox>
            ))}
          </Stack>
        }
      >
        <GhostButton
          ref={triggerRef}
          isActive={isWorkspacesPopoverOpen}
          onClick={() => setIsWorkspacesPopoverOpen(prev => !prev)}
        >
          {t(
            i18nLocale,
            'inventory:datasourceInformation.workspaceManagerWithCount',
            { count: workspaces.length },
            'Workspace Manager ({{count}})'
          )}
          <Icon iconName={IconName.CHEVRON_DOWN} />
        </GhostButton>
      </ControlledPopover>
    </FlexBox>
  );
};
